// components/wizard/ArticleType.jsx
import React, { useState } from 'react';
import { Loader } from 'lucide-react';
import { disciplines, publicArticleTypes } from '../../../types/GlobalVariables';

const articleTypes = publicArticleTypes;

const RadioGroup = ({ name, value, onChange, options = ['Yes', 'No'], error }) => (
  <div className="space-y-2">
    {options.map((label) => {
      const optionValue = label.toLowerCase();
      return (
        <label key={optionValue} className="flex items-center">
          <input
            type="radio"
            name={name}
            value={optionValue}
            checked={value === optionValue}
            onChange={(e) => onChange(e.target.value)}
            className="mr-2"
          />
          {label}
        </label>
      );
    })}
    {error && <p className="mt-1 text-sm text-red-500">{error}</p>}
  </div>
);

const ArticleType = ({ formData, updateFormData, onNext, saving }) => {
  const [errors, setErrors] = useState({});

  const validate = () => {
    const newErrors = {};
    if (!formData.articleType) newErrors.articleType = 'Article type is required';
    if (!formData.discipline) newErrors.discipline = 'Discipline is required';
    if (!formData.isWomenInScience) newErrors.isWomenInScience = 'Please select an option';
    if (!formData.isBelispointAcademic) newErrors.isBelispointAcademic = 'Please select an option';
    if (!formData.isKidnappingForRansom) newErrors.isKidnappingForRansom = 'Please select an option';
    if (!formData.previousSubmission) newErrors.previousSubmission = 'Please select an option';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (saving) return;
    if (validate()) {
      onNext();
    }
  };

  // Handle previousSubmission change — clear previousId when switching to "no"
  const handlePreviousSubmissionChange = (value) => {
    updateFormData({
      previousSubmission: value,
      ...(value === 'no' ? { previousId: '' } : {})
    });
  };

  const radioQuestions = [
    {
      name: 'isWomenInScience',
      label: 'Is this manuscript being submitted to the "Women in Contemporary Science in Africa" special issue?'
    },
    {
      name: 'isBelispointAcademic',
      label: 'Is this manuscript being submitted to the "Bellistpoint Academic" special issue?'
    },
    {
      name: 'isKidnappingForRansom',
      label: 'Is this manuscript related to the issue of kidnapping for ransom?'
    },
    {
      name: 'previousSubmission',
      label: 'Has this manuscript been submitted previously to ASFIRJ?',
      onChange: handlePreviousSubmissionChange
    }
  ];

  return (
    <form onSubmit={handleSubmit}>
      <h3 className="text-xl font-semibold mb-6">Article Type & Discipline</h3>

      <div className="space-y-6">
        {/* Article Type */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Article Type <span className="text-red-500">*</span>
          </label>
          <select
            value={formData.articleType || ''}
            onChange={(e) => updateFormData({ articleType: e.target.value })}
            className={`w-full p-3 border rounded-lg focus:ring-2 focus:ring-[--asfi-blue] ${
              errors.articleType ? 'border-red-500' : 'border-gray-300'
            }`}
          >
            <option value="">Select Article Type</option>
            {articleTypes.map((type) => (
              <option key={type} value={type}>{type}</option>
            ))}
          </select>
          {errors.articleType && (
            <p className="mt-1 text-sm text-red-500">{errors.articleType}</p>
          )}
        </div>

        {/* Discipline */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Discipline <span className="text-red-500">*</span>
          </label>
          <select
            value={formData.discipline || ''}
            onChange={(e) => updateFormData({ discipline: e.target.value })}
            className={`w-full p-3 border rounded-lg focus:ring-2 focus:ring-[--asfi-blue] ${
              errors.discipline ? 'border-red-500' : 'border-gray-300'
            }`}
          >
            <option value="">Select Discipline</option>
            {disciplines.map((discipline) => (
              <option key={discipline} value={discipline}>{discipline}</option>
            ))}
          </select>
          {errors.discipline && (
            <p className="mt-1 text-sm text-red-500">{errors.discipline}</p>
          )}
        </div>

        {/* Radio questions */}
        {radioQuestions.map(({ name, label, onChange }) => (
          <div key={name}>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {label} <span className="text-red-500">*</span>
            </label>
            <RadioGroup
              name={name}
              value={formData[name]}
              onChange={onChange || ((value) => updateFormData({ [name]: value }))}
              error={errors[name]}
            />
          </div>
        ))}

        {/* Previous Manuscript ID */}
        {formData.previousSubmission === 'yes' && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Previous Manuscript ID
            </label>
            <input
              type="text"
              value={formData.previousId || ''}
              onChange={(e) => updateFormData({ previousId: e.target.value })}
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[--asfi-blue]"
              placeholder="Enter previous manuscript ID"
            />
          </div>
        )}

        <p className="text-sm text-red-500">* Indicates Mandatory Fields</p>

        <button
          type="submit"
          disabled={saving}
          className="px-8 py-3 bg-[--asfi-blue] text-white rounded-lg hover:bg-purple-700 disabled:opacity-50"
        >
          {saving ? (
            <>
              <Loader className="w-4 h-4 animate-spin inline mr-2" />
              Saving...
            </>
          ) : (
            'Save & Continue'
          )}
        </button>
      </div>
    </form>
  );
};

export default ArticleType;